import React, { useState, useEffect } from 'react';
import {
  Plus, Edit2, Trash2, Save, RefreshCw, X, Image as ImageIcon
} from 'lucide-react';
import ImageUploader from '../common/ImageUploader';

/**
 * Reusable admin manager for gallery items.
 * Supports types: "travel" | "love" | "submerge"
 */
const GalleryManager = ({ type, title, description, allowCategory = false, allowLocation = false }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [formData, setFormData] = useState({
    image_url: '',
    title: '',
    description: '',
    location: '',
    category: '',
    order: 0,
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const endpoint = type === 'love'
        ? `${API_URL}/api/gallery/admin/love`
        : `${API_URL}/api/gallery/${type}`;
      const response = await fetch(endpoint, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setItems(data);
      }
    } catch (error) {
      console.error(`Error fetching ${type} items:`, error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      image_url: '',
      title: '',
      description: '',
      location: '',
      category: '',
      order: items.length,
    });
    setErrorMsg('');
  };

  const startEditing = (item) => {
    setEditingId(item.gallery_id);
    setFormData({
      image_url: item.image_url || '',
      title: item.title || '',
      description: item.description || '',
      location: item.location || '',
      category: item.category || '',
      order: item.order || 0,
    });
    setShowAddForm(false);
    setErrorMsg('');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setShowAddForm(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.image_url) {
      setErrorMsg('Please upload an image or provide an image URL.');
      return;
    }
    setSaving(true);
    setErrorMsg('');
    try {
      const url = editingId
        ? `${API_URL}/api/gallery/item/${editingId}`
        : `${API_URL}/api/gallery/${type}`;
      const method = editingId ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        await fetchItems();
        cancelEditing();
      } else {
        const err = await response.json().catch(() => ({}));
        setErrorMsg(err.detail || 'Failed to save');
      }
    } catch (error) {
      console.error('Save error:', error);
      setErrorMsg('Network error while saving');
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image? This cannot be undone.')) return;
    try {
      const response = await fetch(`${API_URL}/api/gallery/item/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) await fetchItems();
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid={`gallery-manager-${type}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ImageIcon className="text-blue-600" />
            {title} ({items.length})
          </h2>
          {description && <p className="text-sm text-gray-600 mt-1">{description}</p>}
        </div>
        <button
          data-testid={`add-${type}-image-btn`}
          onClick={() => { setShowAddForm(true); setEditingId(null); resetForm(); }}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg hover:shadow-lg transition-all"
        >
          <Plus size={18} /> Add Image
        </button>
      </div>

      {(showAddForm || editingId) && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">
              {editingId ? 'Edit Image' : 'Add New Image'}
            </h3>
            <button
              type="button"
              onClick={cancelEditing}
              className="text-gray-400 hover:text-gray-700"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <ImageUploader
              label="Image *"
              value={formData.image_url}
              onChange={(url) => setFormData((prev) => ({ ...prev, image_url: url }))}
              placeholder="Upload JPG, JPEG or PNG"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Short title (optional)"
                  data-testid={`${type}-title-input`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData((prev) => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                />
              </div>
              {allowLocation && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData((prev) => ({ ...prev, location: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Varanasi, Uttar Pradesh"
                  />
                </div>
              )}
              {allowCategory && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Heritage, Culture"
                  />
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea
                required
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Describe this image / memory..."
                data-testid={`${type}-description-input`}
              />
            </div>
            {errorMsg && <p className="text-sm text-red-600">{errorMsg}</p>}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={cancelEditing}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid={`save-${type}-image-btn`}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
                {editingId ? 'Update' : 'Add'} Image
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item) => (
          <div
            key={item.gallery_id}
            className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-blue-300 hover:shadow-md transition-all"
          >
            <div className="relative aspect-[4/3] bg-gray-100">
              <img
                src={item.image_url}
                alt={item.title || item.description}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=Image'; }}
              />
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => startEditing(item)}
                  className="p-1.5 bg-white/90 text-blue-600 rounded-lg hover:bg-white shadow"
                  aria-label="Edit"
                >
                  <Edit2 size={15} />
                </button>
                <button
                  onClick={() => deleteItem(item.gallery_id)}
                  className="p-1.5 bg-white/90 text-red-600 rounded-lg hover:bg-white shadow"
                  aria-label="Delete"
                  data-testid={`delete-${type}-${item.gallery_id}`}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
            <div className="p-3">
              {item.title && <h4 className="font-semibold text-gray-900 text-sm truncate">{item.title}</h4>}
              {item.location && <p className="text-xs text-blue-600 mt-0.5">{item.location}</p>}
              <p className="text-xs text-gray-600 mt-1 line-clamp-2">{item.description}</p>
              <p className="text-xs text-gray-400 mt-1">Order: {item.order}</p>
            </div>
          </div>
        ))}
      </div>

      {items.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <ImageIcon size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-600">No images yet.</p>
          <button
            onClick={() => { setShowAddForm(true); resetForm(); }}
            className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
          >
            Add your first image
          </button>
        </div>
      )}
    </div>
  );
};

export default GalleryManager;
